import { injectable } from 'inversify';

import { ILeaguesRepository } from '@/domain/repositories/leagues.repository.interface';
import { DistrictsData, Result } from '@/entities';
import { Division } from '@/entities/Division';
import { FeaturedLeaguesData } from '@/entities/FeaturedLeaguesData';
import { InvitationCodeData } from '@/entities/InvitationCodeData';
import { League } from '@/entities/League';
import { MLPStripData } from '@/entities/MLPStripData';
import { Player } from '@/entities/Player';
import { SearchParams } from '@/entities/SearchParams';
import { Season } from '@/entities/Season';
import { SendMessageData, SendMessagePayload } from '@/entities/sendMessageData';
import { SponsorLevelWithSponsors } from '@/entities/Sponsor';
import { StandingItem } from '@/entities/StandingItem';
import { TeamLeagueInfo } from '@/entities/TeamLeagueInfo';
import { districtsDtoToDistrictsData } from '@/infrastructure/mapper';
import { marketingModuleMapper } from '@/infrastructure/mapper/marketingModule-mapper';
import { recommendedLeaguesToRecommendedLeaguesDto } from '@/infrastructure/mapper/recommended-leagues-mapper';
import { sponsorLevelWithSponsorsDtoToSponsorLevelWithSponsors } from '@/infrastructure/mapper/sponsor.mapper';
import { teamLeagueInfoDtoToTeamLeagueInfo } from '@/infrastructure/mapper/team-league-info.mapper';
import { listDivisions } from '@/infrastructure/network/data/divisions';
import {
	getDistricts,
	getFeaturedLeagues,
	getHpMarketing,
	getInvitationCode,
	getLeaguePlayers,
	getMLPStripEvents,
	getRecommendedLeagues,
	getSponsors,
	sendMessage
} from '@/infrastructure/network/data/leagues';
import { getSeasons, getStandings } from '@/infrastructure/network/data/standings';
import { getTeamLeagueInfo, getTeamLeagueSeasonInfo } from '@/infrastructure/network/data/team-league-info';
import { getTimezones } from '@/infrastructure/network/data/timezones';
import { MarketingModuleDto } from '@/infrastructure/network/dto/HpMarketingModuleDto';
import { TeamLeagueSeasonInfoDto } from '@/infrastructure/network/dto/TeamLeagueSeasonInfoDto';

@injectable()
export class LeaguesRepository implements ILeaguesRepository {
	async getRecommendedLeagues(): Promise<Result<League[]>> {
		const result = await getRecommendedLeagues();
		if (result.statusCode === 200) {
			return { data: recommendedLeaguesToRecommendedLeaguesDto(result.data), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getFeaturedLeagues(moduleFor: number): Promise<Result<FeaturedLeaguesData>> {
		const result = await getFeaturedLeagues({ moduleFor });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getMLPStrip(): Promise<Result<MLPStripData[]>> {
		const result = await getMLPStripEvents();
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getHpMarketingInfo(hpMarketingType: string): Promise<Result<MarketingModuleDto>> {
		const result = await getHpMarketing({ hpMarketingType });
		if (result.statusCode === 200) {
			return { data: marketingModuleMapper(result.data), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getLeaguePlayers(
		organizationUuid: string,
		teamLeagueUuid: string,
		searchParams?: SearchParams,
		sortBy?: string | null,
		orderBy?: string | null
	): Promise<Result<Player[]>> {
		const result = await getLeaguePlayers({ organizationUuid, teamLeagueUuid, searchParams, sortBy, orderBy });
		if (result.statusCode === 200) {
			return {
				data: result.data.players,
				statusCode: result.statusCode,
				totalRecords: result.data.totalRecords,
				currentPage: result.data.currentPage,
				pageSize: result.data.pageSize
			};
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getStandings(
		teamLeagueUuid: string,
		organizationUuid: string,
		seasonId: string,
		searchParams?: SearchParams
	): Promise<Result<StandingItem[]>> {
		const result = await getStandings({ teamLeagueUuid, organizationUuid, seasonId, searchParams });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getSeasons(uuid: string): Promise<Result<Season[]>> {
		const result = await getSeasons({ uuid });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getDistricts(teamLeagueUuid: string, organizationUuid: string): Promise<Result<DistrictsData>> {
		const result = await getDistricts({ teamLeagueUuid, organizationUuid });
		if (result.statusCode === 200) {
			return { data: districtsDtoToDistrictsData(result.data), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getSponsors(teamLeagueUuid: string, moduleFor: number): Promise<Result<SponsorLevelWithSponsors[]>> {
		const result = await getSponsors({ teamLeagueUuid, moduleFor });
		if (result.statusCode === 200) {
			return { data: result.data.map(sponsorLevelWithSponsorsDtoToSponsorLevelWithSponsors), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getLeagueDivisions(teamLeagueUuid: string, organizationUuid: string, searchParams?: SearchParams): Promise<Result<Division[]>> {
		const result = await listDivisions({ teamLeagueUuid, organizationUuid, searchParams });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getInfo(teamLeagueUuid: string, organizationUuid: string): Promise<Result<TeamLeagueInfo>> {
		const [result, timezones] = await Promise.all([getTeamLeagueInfo({ teamLeagueUuid, organizationUuid }), getTimezones()]);

		if (result.statusCode === 200) {
			return { data: teamLeagueInfoDtoToTeamLeagueInfo(result.data, timezones.data), statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async getSeasonInfo(teamLeagueUuid: string, organizationUuid: string): Promise<Result<TeamLeagueSeasonInfoDto>> {
		const result = await getTeamLeagueSeasonInfo({ teamLeagueUuid, organizationUuid });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}
	async getInvitationCode(teamLeagueUuid: string, organizationUuid: string, teamUuid: string): Promise<Result<InvitationCodeData>> {
		const result = await getInvitationCode({ teamLeagueUuid, organizationUuid, teamUuid });
		if (result.statusCode === 200) {
			return { data: result.data, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode, correlationId: result.correlationId, error: result.error };
		}
	}

	async sendMessage(teamLeagueUuid: string, organizationUuid: string, requestPayload: SendMessagePayload): Promise<Result<SendMessageData>> {
		const result = await sendMessage({ teamLeagueUuid, organizationUuid, requestPayload });
		if (result.statusCode === 200) {
			return { data: result, statusCode: result.statusCode };
		} else {
			return { statusCode: result.statusCode };
		}
	}
}
